import { Layout } from "@/components/layout";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useLanguage } from "@/contexts/language";
import { useCreateSession, useGetCategoryStats, getGetCategoryStatsQueryKey } from "@workspace/api-client-react";
import { useTrack, trackSupportsExamSimulation } from "@/contexts/track";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Play, Settings, GraduationCap, Clock, CheckSquare,
  AlertTriangle, X, ShieldAlert, BookOpen, Lock, CheckCircle2, Zap,
  PauseCircle, PlayCircle, Trash2, Target, TrendingUp, Info,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getExamRuleForTrack } from "@/lib/tc-exam-rules";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/contexts/subscription";
import { useAccountPaused } from "@/hooks/useAccountPaused";

const EXAM_MIN_PRACTICE = 200;

export default function Practice() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { selectedTrack } = useTrack();
  const { getPlanForTrack } = useSubscription();
  const plan = getPlanForTrack(selectedTrack);
  const examModeAvailable = trackSupportsExamSimulation(selectedTrack);
  const examRule = getExamRuleForTrack(selectedTrack);
  const { paused, pauseExpiresAt, loading: pauseLoading } = useAccountPaused();
  const p = t.practice;
  const [mode, setMode] = useState<"random" | "category" | "exam" | "weakness" | "ladder">("random");
  const [category, setCategory] = useState<string>("");
  const [count, setCount] = useState<string>("10");
  const [showBriefing, setShowBriefing] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [resumeSessionId, setResumeSessionId] = useState<number | null>(null);
  const [resumeChecking, setResumeChecking] = useState(true);
  const [discardingResume, setDiscardingResume] = useState(false);

  // Resolve unfinished session from server first (account-level across devices),
  // then sync local cache to prevent stale-device resume loops.
  useEffect(() => {
    const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
    const token = localStorage.getItem("token");
    const saved = localStorage.getItem("cpl_active_session");
    const sid = saved ? parseInt(saved, 10) : 0;

    fetch(`${baseUrl}/api/sessions/active?track=${encodeURIComponent(selectedTrack)}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "Content-Type": "application/json" },
      credentials: "include",
    })
      .then(r => r.ok ? r.json() : null)
      .then((data: { session?: { id: number } | null } | null) => {
        const activeId = data?.session?.id ?? null;
        if (activeId) {
          setResumeSessionId(activeId);
          localStorage.setItem("cpl_active_session", String(activeId));
        } else {
          localStorage.removeItem("cpl_active_session");
        }
      })
      .catch(() => {
        // Fallback: if active lookup fails, validate local cached session id.
        if (!sid) {
          localStorage.removeItem("cpl_active_session");
          setResumeSessionId(null);
          return;
        }
        fetch(`${baseUrl}/api/sessions/${sid}`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "Content-Type": "application/json" },
          credentials: "include",
        })
          .then(r => r.ok ? r.json() : null)
          .then((localData: { completedAt?: string | null } | null) => {
            if (localData && !localData.completedAt) {
              setResumeSessionId(sid);
            } else {
              localStorage.removeItem("cpl_active_session");
              setResumeSessionId(null);
            }
          })
          .catch(() => {
            localStorage.removeItem("cpl_active_session");
            setResumeSessionId(null);
          })
          .finally(() => setResumeChecking(false));
      })
      .finally(() => setResumeChecking(false));
  }, [selectedTrack]);

  useEffect(() => {
    if (!examModeAvailable) {
      setMode((m) => (m === "exam" ? "random" : m));
      setShowBriefing(false);
      setShowLockModal(false);
    }
  }, [examModeAvailable]);

  const { data: categories, isLoading: categoriesLoading } = useGetCategoryStats(
    { track: selectedTrack },
    { query: { queryKey: getGetCategoryStatsQueryKey({ track: selectedTrack }), staleTime: 300_000 } }
  );

  const createSession = useCreateSession();

  const planRank = { free: 0, pro: 1, pro_plus: 2 };
  const userRank = planRank[plan as keyof typeof planRank] ?? 0;
  const isFree = plan === "free";

  const countOptions = isFree
    ? [
        { value: "10", label: "10 Questions" },
        { value: "20", label: "20 Questions" },
        { value: "40", label: "40 Questions (max)" },
      ]
    : [
        { value: "10", label: "10 Questions" },
        { value: "25", label: "25 Questions" },
        { value: "50", label: "50 Questions" },
        { value: "100", label: "100 Questions" },
      ];

  const availableCategories = categories ?? [];
  const lockedSections = categories?.filter(c => c.totalCorrect < EXAM_MIN_PRACTICE) ?? [];
  const examUnlocked =
    examModeAvailable
    && !categoriesLoading
    && (categories?.length ?? 0) > 0
    && lockedSections.length === 0;

  const handleStart = () => {
    if (mode === "exam" && userRank < 2) {
      setLocation("/subscription");
      return;
    }

    if (mode === "exam") {
      if (!examUnlocked) {
        setShowLockModal(true);
        return;
      }
      setShowBriefing(true);
      return;
    }

    if ((mode === "category" || mode === "ladder") && !category) {
      toast({
        title: p.categoryRequired,
        description: p.categoryRequiredDesc,
        variant: "destructive",
      });
      return;
    }

    launchSession();
  };

  const launchSession = () => {
    createSession.mutate({
      data: {
        mode,
        category: mode === "category" || mode === "ladder" ? category : undefined,
        count: mode === "exam" ? examRule.questions : parseInt(count, 10),
        licenseTrack: selectedTrack,
      }
    }, {
      onSuccess: (session) => setLocation(`/session/${session.id}`),
      onError: (err: unknown) => {
        const typedErr = err as { response?: { status?: number }; status?: number };
        const status = typedErr?.response?.status ?? typedErr?.status;
        if (status === 402) {
          setLocation("/subscription");
          return;
        }
        const message = err instanceof Error ? err.message : p.sessionFailDesc;
        toast({ title: p.sessionFailTitle, description: message || p.sessionFailDesc, variant: "destructive" });
      }
    });
  };

  const modes = [
    { key: "random",   icon: Play,         labelKey: "modeRandom"   as const, descKey: "modeRandomDesc"   as const, minPlan: "free"     },
    { key: "category", icon: Settings,     labelKey: "modeCategory" as const, descKey: "modeCategoryDesc" as const, minPlan: "free"     },
    { key: "ladder",   icon: TrendingUp,   labelKey: "modeLadder"   as const, descKey: "modeLadderDesc"   as const, minPlan: "free"     },
    { key: "weakness", icon: Target,       labelKey: "modeWeakness" as const, descKey: "modeWeaknessDesc" as const, minPlan: "free"     },
    { key: "exam",     icon: GraduationCap,labelKey: "modeExam"     as const, descKey: "modeExamDesc"     as const, minPlan: "pro_plus" },
  ] as const;

  const visibleModes = examModeAvailable ? modes : modes.filter((m) => m.key !== "exam");

  if (!pauseLoading && paused) {
    const resumeDate = pauseExpiresAt ? new Date(pauseExpiresAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : null;
    return (
      <Layout>
        <div className="max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[50vh] text-center gap-5 animate-in fade-in duration-500">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30">
            <PauseCircle className="w-8 h-8 text-amber-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-mono tracking-tight text-foreground mb-2">Account Paused</h2>
            <p className="text-sm text-muted-foreground font-mono max-w-sm">
              Practice sessions are not available while your account is paused.
              {resumeDate ? ` Your account will automatically resume on ${resumeDate}.` : ""}
            </p>
          </div>
          <p className="text-xs text-muted-foreground/60 font-mono">Your progress is safe. All other areas of Flight Deck are still accessible.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="study-layout max-w-2xl mx-auto space-y-8 animate-in fade-in duration-500">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{p.title}</h1>
          <p className="text-muted-foreground">{p.subtitle}</p>
        </div>

        {/* Resume session banner */}
        {!resumeChecking && resumeSessionId && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <PlayCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-foreground">You have an unfinished session</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Session #{resumeSessionId} — pick up where you left off, or discard it to start fresh.
                </p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                onClick={() => setLocation(`/session/${resumeSessionId}`)}
                className="gap-1.5"
              >
                <PlayCircle className="w-3.5 h-3.5" /> Continue
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={discardingResume}
                onClick={async () => {
                  if (!resumeSessionId) return;
                  setDiscardingResume(true);
                  try {
                    const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
                    const token = localStorage.getItem("token");
                    await fetch(`${baseUrl}/api/sessions/active/discard`, {
                      method: "PATCH",
                      headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        "Content-Type": "application/json",
                      },
                      credentials: "include",
                      body: JSON.stringify({ track: selectedTrack }),
                    });
                  } catch {
                    // Non-fatal: still clear local resume pointer.
                  } finally {
                    localStorage.removeItem("cpl_active_session");
                    setResumeSessionId(null);
                    setDiscardingResume(false);
                  }
                }}
                className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
              >
                <Trash2 className="w-3.5 h-3.5" /> {discardingResume ? "Discarding..." : "Discard"}
              </Button>
            </div>
          </div>
        )}

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              {p.missionParams}
            </CardTitle>
            <CardDescription>{p.missionParamsDesc}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">

            {/* Mode selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{p.trainingMode}</label>
              <div
                className={cn(
                  "grid grid-cols-2 sm:grid-cols-3 gap-3",
                  examModeAvailable ? "lg:grid-cols-5" : "lg:grid-cols-4",
                )}
              >
                {visibleModes.map(({ key, icon: Icon, labelKey, descKey, minPlan }) => {
                  const label = p[labelKey];
                  const desc = p[descKey];
                  const reqRank = planRank[minPlan as keyof typeof planRank] ?? 0;
                  const locked = userRank < reqRank;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setMode(key)}
                      className={cn(
                        "relative flex flex-col items-center justify-center p-4 rounded-md border transition-all text-center",
                        locked && "opacity-60",
                        mode === key
                          ? key === "exam"
                            ? "border-yellow-500/70 bg-yellow-500/10 text-yellow-400"
                            : "border-primary bg-primary/10 text-primary"
                          : "border-border bg-background hover:border-primary/50 text-foreground"
                      )}
                    >
                      {locked && (
                        <span className="absolute top-1.5 right-1.5 flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-400 bg-amber-500/20 border border-amber-500/30 rounded px-1 py-0.5">
                          <Zap className="w-2.5 h-2.5" />Pro+
                        </span>
                      )}
                      <Icon className="w-6 h-6 mb-2" />
                      <span className="font-semibold text-sm">{label}</span>
                      <span className="text-xs opacity-70 mt-1">{desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Climb the Ladder — how it works (same idea as exam info panel) */}
            {mode === "ladder" && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                <div className="flex items-start gap-2 p-3 bg-primary/10 border border-primary/30 rounded-md">
                  <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-xs text-primary/90 font-mono leading-relaxed">
                    {p.modeLadderFormatNote}
                  </p>
                </div>
                <div className="border border-primary/30 rounded-md overflow-hidden">
                  <div className="bg-primary/10 px-4 py-3 flex items-center gap-2 border-b border-primary/20">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm text-primary font-mono tracking-wide">{p.modeLadderPanelTitle}</span>
                  </div>
                  <div className="p-4 space-y-3">
                    <p className="text-sm text-muted-foreground leading-relaxed">{p.modeLadderPanelBody1}</p>
                    <p className="text-xs text-muted-foreground font-mono leading-relaxed border-t border-border pt-3">
                      {p.modeLadderPanelBody2}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Category selector (By Subject + Climb the Ladder) */}
            {(mode === "category" || mode === "ladder") && (
              <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                <label className="text-sm font-medium">{p.selectSubject}</label>
                {categoriesLoading ? <Skeleton className="h-10 w-full" /> : (
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="w-full bg-background border-border">
                      <SelectValue placeholder={p.selectSubjectPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategories.map((cat) => (
                        <SelectItem key={cat.category} value={cat.category}>
                          {cat.category} ({cat.totalQuestions} questions)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Full Exam info panel */}
            {mode === "exam" && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
                  <ShieldAlert className="w-4 h-4 text-yellow-400 shrink-0" />
                  <p className="text-xs text-yellow-400 font-mono">
                    {p.examFormatNote}
                  </p>
                </div>

                <div className="border border-yellow-500/30 rounded-md overflow-hidden">
                  <div className="bg-yellow-500/10 px-4 py-3 flex items-center gap-2 border-b border-yellow-500/20">
                    <GraduationCap className="w-4 h-4 text-yellow-400" />
                    <span className="font-semibold text-sm text-yellow-400 font-mono">{examRule.label.toUpperCase()}</span>
                  </div>
                  <div className="p-4 grid grid-cols-3 gap-4">
                    {[
                      { icon: CheckSquare, label: p.examQuestionsLabel, value: String(examRule.questions) },
                      { icon: Clock, label: p.examTimeLimitLabel, value: `${examRule.timeHours}h` },
                      { icon: BookOpen, label: p.examPassMarkLabel, value: `${examRule.passPercent}%` },
                    ].map(({ icon: Icon, label, value }) => (
                      <div key={label} className="flex flex-col items-center text-center gap-1">
                        <Icon className="w-4 h-4 text-yellow-400/60" />
                        <span className="text-xl font-bold font-mono text-yellow-400">{value}</span>
                        <span className="text-xs text-muted-foreground font-mono">{label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 pb-4">
                    <p className="text-xs text-muted-foreground">{examRule.description}</p>
                  </div>
                </div>

                {categoriesLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : (
                  <div className="border border-border rounded-md overflow-hidden">
                    <div className="px-4 py-2.5 bg-secondary/40 border-b border-border flex items-center justify-between">
                      <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{p.sectionReadiness}</span>
                      <span className="text-xs font-mono text-muted-foreground">{p.minCorrectEach.replace("{n}", String(EXAM_MIN_PRACTICE))}</span>
                    </div>
                    <div className="divide-y divide-border">
                      {categories?.map((cat) => {
                        const progress = Math.min(cat.totalCorrect / EXAM_MIN_PRACTICE, 1);
                        const ready = cat.totalCorrect >= EXAM_MIN_PRACTICE;
                        return (
                          <div key={cat.category} className="px-4 py-3">
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2">
                                {ready
                                  ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                                  : <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                                }
                                <span className={cn("text-sm font-mono", ready ? "text-foreground" : "text-muted-foreground")}>
                                  {cat.category}
                                </span>
                              </div>
                              <span className={cn("text-xs font-mono", ready ? "text-primary" : "text-muted-foreground")}>
                                {cat.totalCorrect} / {EXAM_MIN_PRACTICE} {p.correctLabel}
                                {ready && " ✓"}
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                              <div
                                className={cn("h-full rounded-full transition-all", ready ? "bg-primary" : "bg-yellow-500/60")}
                                style={{ width: `${progress * 100}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className={cn(
                      "flex items-center justify-between px-4 py-2.5 border-t border-border text-xs font-mono",
                      examUnlocked ? "bg-primary/5 text-primary" : "bg-secondary/40 text-muted-foreground"
                    )}>
                      <span>{examUnlocked ? p.allSectionsReady : p.sectionsNeedPractice.replace("{n}", String(lockedSections.length)).replace("{s}", lockedSections.length !== 1 ? "s" : "").replace("{s2}", lockedSections.length !== 1 ? "nt" : "")}</span>
                      <span>{examUnlocked ? p.examUnlockedLabel : p.examLockedLabel}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Question count (not for exam) */}
            {mode !== "exam" && (
              <div className="space-y-3">
                <label className="text-sm font-medium">{p.questionCountLabel}</label>
                <div className="space-y-1.5">
                  <Select value={count} onValueChange={setCount}>
                    <SelectTrigger className="w-full md:w-[200px] bg-background border-border">
                      <SelectValue placeholder={p.selectCount} />
                    </SelectTrigger>
                    <SelectContent>
                      {countOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isFree && (
                    <p className="text-xs text-muted-foreground font-mono">
                      {p.freePlanNote}{" "}
                      <a href="/subscription" className="text-primary underline-offset-2 hover:underline">{p.upgradeForMore}</a>
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="pt-4 flex flex-col gap-2">
              {resumeSessionId && (
                <p className="text-xs text-muted-foreground font-mono text-right">
                  Continue or discard your saved session above before starting a new one.
                </p>
              )}
              <div className="flex justify-end">
              <Button
                onClick={handleStart}
                disabled={createSession.isPending || !!resumeSessionId}
                size="lg"
                className={cn(
                  "study-practice-launch w-full md:w-auto",
                  mode === "exam" && examUnlocked && "bg-yellow-500 hover:bg-yellow-400 text-black font-bold",
                  mode === "exam" && !examUnlocked && "opacity-60 cursor-pointer",
                )}
              >
                {mode === "exam" && !examUnlocked && <Lock className="w-4 h-4 mr-2" />}
                {createSession.isPending
                  ? p.initializing
                  : mode === "exam"
                    ? examUnlocked ? p.proceedToBriefing : p.examLocked
                    : p.commenceSession}
              </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exam pre-briefing modal */}
      {showBriefing && examModeAvailable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowBriefing(false)} />
          <div className="relative z-10 bg-card border border-yellow-500/40 rounded-lg w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-yellow-500/10 border-b border-yellow-500/20 p-5 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-500/20 p-2 rounded-md shrink-0">
                  <GraduationCap className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="font-bold text-foreground font-mono tracking-wide">{p.examBriefingTitle}</h2>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{p.examBriefingClosed}</p>
                </div>
              </div>
              <button onClick={() => setShowBriefing(false)} className="text-muted-foreground hover:text-foreground p-1 shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {p.examBriefingBody
                  .replace("{questions}", String(examRule.questions))
                  .replace("{hours}", String(examRule.timeHours))}
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => { setShowBriefing(false); launchSession(); }}
                  disabled={createSession.isPending}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                >
                  {createSession.isPending ? p.initializing : p.beginExamination}
                </Button>
                <Button variant="outline" onClick={() => setShowBriefing(false)} className="w-full">
                  {p.cancel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lock explanation modal */}
      {showLockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowLockModal(false)} />
          <div className="relative z-10 bg-card border border-border rounded-lg w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-secondary/60 border-b border-border p-5 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-500/20 p-2 rounded-md shrink-0">
                  <Lock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="font-bold text-foreground font-mono tracking-wide">{p.examModalTitle}</h2>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{p.examModalSubtitle}</p>
                </div>
              </div>
              <button onClick={() => setShowLockModal(false)} className="text-muted-foreground hover:text-foreground p-1 shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-muted-foreground">
                {p.examModalBody.replace("{n}", String(EXAM_MIN_PRACTICE))}
              </p>
              <div className="space-y-2">
                {lockedSections.map((sec) => {
                  const remaining = EXAM_MIN_PRACTICE - sec.totalCorrect;
                  const progress = Math.min(sec.totalCorrect / EXAM_MIN_PRACTICE, 1);
                  return (
                    <div key={sec.category} className="p-3 bg-secondary/40 border border-border rounded-md">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-mono text-foreground">{sec.category}</span>
                        <span className="text-xs font-mono text-yellow-400">{remaining} {p.moreCorrectNeeded}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-1">
                        <div className="h-full rounded-full bg-yellow-500/60 transition-all" style={{ width: `${progress * 100}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground font-mono">{sec.totalCorrect} / {EXAM_MIN_PRACTICE} {p.correctlyAnsweredCount}</span>
                    </div>
                  );
                })}
              </div>
              <Button onClick={() => setShowLockModal(false)} variant="outline" className="w-full">{p.close}</Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
